import { Channel, CHANNEL_LABEL, eur, SKIP_REASON } from '../../channels/model';
import { useEffect, useState } from 'react';
import { IconPlus, IconTrash, IconX } from '../../components/icons';
import { Field, useToast } from '../../components/ui';
import { EXIT_REASON, HOLD_REASON, outcomeLabel, outcomeTone, SimulationResult, STEP_LABEL, StepType, TraceEntry, VersionRow } from '../../journeys/model';
import { api, Contact, Paged } from '../../lib/api';
import { currentLang, currentLocale, t } from '../../lib/i18n';
import { errorText } from '../Contacts';

interface SynthRow {
  after: string;
  unit: 'minutes' | 'hours' | 'days';
  name: string;
  properties: string;
  contact: string;
}

const STATUS_WORD: Record<string, string> = {
  completed: 'дошёл до конца',
  exited: 'вышел',
  control: 'контрольная группа — писем нет',
  not_eligible: 'не проходит фильтр входа',
  in_progress: 'ещё в пути на горизонте',
};

/** Decimal separator of the interface language: a comma in Russian and Ukrainian, a point in English. */
const dec = (s: string) => (currentLang() === 'en' ? s : s.replace('.', ','));

function offset(from: string, at: string): string {
  const m = Math.round((new Date(at).getTime() - new Date(from).getTime()) / 60_000);
  if (m <= 0) return t('сразу');
  if (m < 60) return t('+{m} мин', { m });
  const h = m / 60;
  if (h < 48) return t('+{h} ч', { h: Number.isInteger(h) ? h : dec(h.toFixed(1)) });
  return t('+{d} дн.', { d: dec((h / 24).toFixed(1)) });
}

function detailText(e: TraceEntry): string {
  const d = e.detail ?? {};
  const parts: string[] = [];
  if (typeof d.reason === 'string') parts.push(t(HOLD_REASON[d.reason] ?? EXIT_REASON[d.reason] ?? d.reason));
  if (typeof d.until === 'string') parts.push(t('до {d}', { d: new Date(d.until).toLocaleString(currentLocale(), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) }));
  if (typeof d.event === 'string') parts.push(d.event);
  if (typeof d.note === 'string') parts.push(d.note);
  return parts.join(' · ');
}

/** «Погружение капли»: one contact's way through the journey on a virtual clock. */
export function DropDive({ start, trace }: { start: string; trace: TraceEntry[] }) {
  return (
    <div className="dive drop-dive">
      {trace.map((e, i) => (
        <div key={i} className={`dive-item ${outcomeTone(e.outcome)}${e.outcome === 'control_assigned' ? ' control' : ''}`} style={{ animationDelay: `${i * 50}ms` }}>
          <div className="row">
            <span className="t">{t(outcomeLabel(e.outcome))}</span>
            <span className="spacer" />
            <span className="ms">{offset(start, e.at)}</span>
          </div>
          <div className="m">
            {e.step ? <>{t('шаг')} <code>{e.step}</code>{e.type ? ` · ${t(STEP_LABEL[e.type as StepType] ?? e.type)}` : ''}</> : t('вход')}
            {detailText(e) && <> · {detailText(e)}</>}
          </div>
          {e.email && (
            <div className="drop-email">
              <b>{e.email.subject ?? t('(тема не отрисована)')}</b>
              <div className="muted">{e.email.template} v{e.email.version} · {t('от {f}', { f: e.email.from })}</div>
              {e.email.missing_variables.length > 0 && <div style={{ color: '#d9433d' }}>{t('Не хватает: {list}', { list: e.email.missing_variables.join(', ') })}</div>}
            </div>
          )}
          {e.notification && (
            <div className="drop-email">
              <b>{e.notification.policy.key} v{e.notification.policy.version} · {e.notification.template.key} v{e.notification.template.version}</b>
              {e.notification.error ? (
                <div style={{ color: '#d9433d' }}>{e.notification.error.message}</div>
              ) : (
                <div className="muted">
                  {e.notification.steps.map((x) => `${t(CHANNEL_LABEL[x.channel as Channel] ?? x.channel)}: ${x.skip_reason ? t('пропуск ({r})', { r: t(SKIP_REASON[x.skip_reason] ?? x.skip_reason) }) : x.state === 'planned' ? t('в плане, {c}', { c: eur(x.estimated_cost) }) : x.state}`).join(' → ')}
                  {e.notification.estimated_cost_max && <> · {t('худший случай {a}', { a: eur(e.notification.estimated_cost_max.amount) })}</>}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Simulation (docs/journeys/SPEC.md §3.15, §4.1): chosen or random contacts, synthetic events («через 2 ч — order.paid»),
 * the result is a vertical trace per contact. Nothing is sent and nothing is written.
 */
export function JourneySimulate({ journeyId, versions, defaultVersion }: { journeyId: string; versions: VersionRow[]; defaultVersion: number | null }) {
  const toast = useToast();
  const [version, setVersion] = useState<number | null>(defaultVersion);
  const [mode, setMode] = useState<'pick' | 'sample'>('pick');
  const [q, setQ] = useState('');
  const [found, setFound] = useState<Contact[]>([]);
  const [picked, setPicked] = useState<Contact[]>([]);
  const [rows, setRows] = useState<SynthRow[]>([]);
  const [group, setGroup] = useState<'' | 'treatment' | 'control'>('');
  const [horizon, setHorizon] = useState(14);
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => setVersion(defaultVersion), [defaultVersion]);
  useEffect(() => {
    if (q.trim().length < 2) {
      setFound([]);
      return;
    }
    const tm = setTimeout(() => {
      api.get<Paged<Contact>>(`/contacts?q=${encodeURIComponent(q.trim())}&limit=8`).then((r) => setFound(r.items)).catch(() => setFound([]));
    }, 250);
    return () => clearTimeout(tm);
  }, [q]);

  async function run() {
    setBusy(true);
    try {
      const events = rows
        .filter((r) => r.name.trim())
        .map((r) => ({
          name: r.name.trim(),
          ...(r.properties.trim() ? { properties: JSON.parse(r.properties) } : {}),
          after: { [r.unit]: Number(r.after) || 0 },
          ...(r.contact !== '' ? { contact_index: Number(r.contact) } : {}),
        }));
      const body = {
        ...(version ? { version } : {}),
        ...(mode === 'pick' ? { contacts: picked.map((c) => c.id) } : { sample: 20 }),
        events,
        horizon_days: horizon,
        ...(group ? { force_group: group } : {}),
      };
      setResult(await api.post<SimulationResult>(`/journeys/${journeyId}/simulate`, body));
    } catch (err) {
      toast(err instanceof SyntaxError ? t('Свойства события: JSON не разбирается') : errorText(err), 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid sim-grid">
      <div className="card card-pad stack" style={{ minWidth: 0 }}>
        <div className="callout ok"><span><b>{t('Ничего не отправляется.')}</b> {t('Расчёт на виртуальных часах: ни одной записи в базе и ни одного письма.')}</span></div>
        <Field label={t('Версия')}>
          <select className="select input" value={version ?? ''} onChange={(e) => setVersion(Number(e.target.value) || null)}>
            {versions.map((v) => <option key={v.version} value={v.version}>v{v.version}{v.published_at ? ` · ${t('опубликована')}` : ''}</option>)}
          </select>
        </Field>
        <div className="chips">
          <button type="button" className={`chip${mode === 'pick' ? ' on' : ''}`} onClick={() => setMode('pick')}>{t('Выбрать контакты')}</button>
          <button type="button" className={`chip${mode === 'sample' ? ' on' : ''}`} onClick={() => setMode('sample')}>{t('20 случайных')}</button>
        </div>
        {mode === 'pick' ? (
          <>
            <input className="input" placeholder={t('Поиск: email или external_id')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('Поиск контакта')} />
            {found.length > 0 && (
              <div className="chips">
                {found.filter((c) => !picked.some((p) => p.id === c.id)).map((c) => (
                  <button key={c.id} type="button" className="chip" disabled={picked.length >= 20} onClick={() => setPicked((p) => [...p, c])}><IconPlus size={12} /> {c.email ?? c.external_id}</button>
                ))}
              </div>
            )}
            {picked.length > 0 ? (
              <div className="chips">
                {picked.map((c, i) => (
                  <span key={c.id} className="chip on">#{i} {c.email ?? c.external_id} <button type="button" className="btn btn-ghost icon-btn" style={{ padding: 0, minHeight: 0 }} aria-label={t('Убрать')} onClick={() => setPicked((p) => p.filter((x) => x.id !== c.id))}><IconX size={12} /></button></span>
                ))}
              </div>
            ) : (
              <div className="small muted">{t('Добавьте до 20 контактов.')}</div>
            )}
          </>
        ) : (
          <div className="small muted">{t('Случайные контакты, проходящие фильтр входа сценария.')}</div>
        )}
        <Field label={t('Синтетические события')} hint={t('Подставляются в ожидания и цели; «кому» — номер контакта из списка или всем')}>
          <div className="stack" style={{ gap: 6 }}>
            {rows.map((r, i) => (
              <div key={i} className="inline-fields">
                <span className="small muted">{t('через')}</span>
                <input className="input" style={{ width: 64 }} type="number" min={0} value={r.after} onChange={(e) => setRows((xs) => xs.map((x, j) => (j === i ? { ...x, after: e.target.value } : x)))} aria-label={t('Через')} />
                <select className="select input" value={r.unit} onChange={(e) => setRows((xs) => xs.map((x, j) => (j === i ? { ...x, unit: e.target.value as SynthRow['unit'] } : x)))} aria-label={t('Единица')}>
                  <option value="minutes">{t('мин')}</option>
                  <option value="hours">{t('ч')}</option>
                  <option value="days">{t('дн.')}</option>
                </select>
                <input className="input input-mono" style={{ width: 130 }} value={r.name} onChange={(e) => setRows((xs) => xs.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} aria-label={t('Событие')} />
                <input className="input input-mono" style={{ flex: 1, minWidth: 120 }} placeholder='{"cart_id":"c1"}' value={r.properties} onChange={(e) => setRows((xs) => xs.map((x, j) => (j === i ? { ...x, properties: e.target.value } : x)))} aria-label={t('Свойства')} />
                <select className="select input" value={r.contact} onChange={(e) => setRows((xs) => xs.map((x, j) => (j === i ? { ...x, contact: e.target.value } : x)))} aria-label={t('Кому')}>
                  <option value="">{t('всем')}</option>
                  {Array.from({ length: mode === 'pick' ? picked.length : 20 }, (_, k) => <option key={k} value={k}>#{k}</option>)}
                </select>
                <button type="button" className="btn btn-sm btn-ghost" aria-label={t('Убрать событие')} onClick={() => setRows((xs) => xs.filter((_, j) => j !== i))}><IconTrash size={14} /></button>
              </div>
            ))}
            <button type="button" className="btn btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setRows((xs) => [...xs, { after: '2', unit: 'hours', name: 'order.paid', properties: '', contact: '' }])}><IconPlus size={14} /> {t('Событие')}</button>
          </div>
        </Field>
        <div className="inline-fields">
          <label className="small">{t('Группа')}
            <select className="select input" style={{ marginLeft: 6 }} value={group} onChange={(e) => setGroup(e.target.value as typeof group)}>
              <option value="">{t('как назначит сценарий')}</option>
              <option value="treatment">{t('получает письма')}</option>
              <option value="control">{t('контрольная')}</option>
            </select>
          </label>
          <label className="small">{t('Горизонт, дн.')}
            <input className="input" style={{ width: 70, marginLeft: 6 }} type="number" min={1} max={30} value={horizon} onChange={(e) => setHorizon(Number(e.target.value))} />
          </label>
        </div>
        <button className="btn btn-primary" style={{ alignSelf: 'flex-start' }} disabled={busy || (mode === 'pick' && !picked.length)} onClick={run}>{busy ? t('Считаю…') : t('Запустить симуляцию')}</button>
      </div>
      <div className="stack" style={{ minWidth: 0 }}>
        {!result ? (
          <div className="card card-pad small muted">{t('Здесь появится «погружение капли»: путь каждого контакта по шагам с временем, исходами и письмами.')}</div>
        ) : !result.contacts.length ? (
          <div className="card card-pad">{t('Нет подходящих контактов.')}</div>
        ) : (
          result.contacts.map((c, i) => (
            <div key={c.contact.id} className="card">
              <div className="card-head" style={{ flexWrap: 'wrap' }}>
                <h3 className="drop-head" style={{ overflowWrap: 'anywhere' }}>#{i} {c.contact.email ?? c.contact.external_id}</h3>
                <span className={`pill ${c.status === 'completed' ? 'ok' : c.status === 'control' ? 'queued' : c.status === 'exited' ? 'warn' : 'cancelled'}`}>{t(STATUS_WORD[c.status] ?? c.status)}{c.exit_reason ? `: ${t(EXIT_REASON[c.exit_reason] ?? c.exit_reason)}` : ''}</span>
              </div>
              <div className="card-pad stack">
                {c.warnings.map((w) => <div key={w} className="callout warn small">{w}</div>)}
                <DropDive start={result.start_at} trace={c.trace} />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
